import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './dialog';
import { Button } from './button';
import { AlertCircle, CheckCircle, Info, XCircle } from 'lucide-react';

/**
 * Reusable Confirmation Dialog Component
 * 
 * Usage:
 * <ConfirmDialog
 *   open={isOpen}
 *   onOpenChange={setIsOpen}
 *   title="Delete User"
 *   description="Are you sure you want to delete this user? This action cannot be undone."
 *   onConfirm={handleDelete}
 *   confirmText="Delete"
 *   variant="destructive"
 * />
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  onConfirm,
  onCancel,
  confirmText = "Confirm",
  cancelText = "Cancel",
  variant = "default", // "default" | "destructive" | "success" | "warning"
  loading = false,
  showIcon = true,
  children
}) {
  const handleConfirm = () => {
    onConfirm?.();
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      onOpenChange?.(false);
    }
  };

  const getIcon = () => {
    if (!showIcon) return null;
    
    switch (variant) {
      case 'destructive':
        return <XCircle className="h-6 w-6 text-red-500" />;
      case 'success':
        return <CheckCircle className="h-6 w-6 text-green-500" />;
      case 'warning':
        return <AlertCircle className="h-6 w-6 text-yellow-500" />;
      default:
        return <Info className="h-6 w-6 text-blue-500" />;
    }
  };

  const getButtonVariant = () => {
    switch (variant) {
      case 'destructive':
        return 'destructive';
      case 'success':
        return 'default';
      case 'warning':
        return 'warning';
      default:
        return 'default';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          {showIcon && (
            <div className="flex items-center gap-3 mb-2">
              {getIcon()}
              <DialogTitle className="text-brand-dark">{title}</DialogTitle>
            </div>
          )}
          {!showIcon && <DialogTitle className="text-brand-dark">{title}</DialogTitle>}
          {description && (
            <DialogDescription className="text-brand-dark/70">
              {description}
            </DialogDescription>
          )}
        </DialogHeader>
        
        {children && (
          <div className="py-4">
            {children}
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            type="button"
            variant="outline"
            onClick={handleCancel}
            disabled={loading}
          >
            {cancelText}
          </Button>
          <Button
            type="button"
            variant={getButtonVariant()}
            onClick={handleConfirm}
            disabled={loading}
            className={variant === 'warning' ? 'bg-orange-500 hover:bg-orange-600 text-white' : ''}
          >
            {loading ? 'Processing...' : confirmText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ConfirmDialog;
